import React, { useEffect, useState } from 'react';
import {
  Alert, AlertIcon, Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader,
  ModalOverlay, Spinner, Text, useToast,
} from '@chakra-ui/react';
import axios from 'axios';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

interface Preview { pmName?: string | null; pmEmail?: string | null; projectName?: string | null; defaulted?: boolean; jobLabel?: string | null }

interface Props { workOrderId: string; isOpen: boolean; onClose: () => void }

// item 1364 (Chad: "a pop-up after the initial save that asks if you want to email the project
// manager") — replaces the 1265 form toggle. Shown once, right after a standalone WO's first save.
const PmNotifyPromptModal: React.FC<Props> = ({ workOrderId, isOpen, onClose }) => {
  const toast = useToast();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isOpen || !workOrderId) return;
    setLoading(true); setPreview(null);
    axios.get(`${apiUrl()}WorkOrder/${workOrderId}/pm-notify-preview`)
      .then((r) => setPreview(r.data))
      .catch(() => setPreview({}))
      .finally(() => setLoading(false));
  }, [isOpen, workOrderId]);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${apiUrl()}WorkOrder/${workOrderId}/notify-pm`, {});
      toast({ status: 'success', title: `New-job notice sent to ${data?.pmName ?? 'the project manager'}.` });
      onClose();
    } catch (e: any) {
      toast({
        status: 'error', title: 'Could not send', description: String(e?.response?.data ?? e?.message ?? 'Try again.'), duration: 9000, isClosable: true,
      });
    } finally { setSending(false); }
  };

  const hasPm = !!preview?.pmEmail;
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Email the project manager?</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loading && <Spinner size="sm" />}
          {!loading && hasPm && (
            <>
              <Text fontSize="sm">
                {`Work order saved. Send ${preview?.pmName ?? 'the project manager'} (${preview?.pmEmail}) the new-job notice for `}
                <b>{preview?.jobLabel ?? 'this job'}</b>
                {preview?.projectName ? ` on ${preview.projectName}` : ''}
                ?
              </Text>
              {preview?.defaulted && (
                <Text fontSize="xs" color="gray.500" mt={2}>
                  No PM is assigned on the project yet —
                  {preview?.pmName}
                  {' '}
                  is the company&apos;s only flagged project manager, so sending also assigns them.
                </Text>
              )}
              <Text fontSize="xs" color="gray.500" mt={2}>You can always send it later from the work order. Nothing goes to the client.</Text>
            </>
          )}
          {!loading && !hasPm && (
            <Alert status="info" fontSize="sm" borderRadius="md">
              <AlertIcon />
              No project manager is assigned to this project (and there isn&apos;t a single company-wide default), so there&apos;s no one to email. Assign a PM on the project to send the new-job notice.
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>{hasPm ? 'Not now' : 'Close'}</Button>
          {hasPm && <Button colorScheme="red" onClick={send} isLoading={sending}>Send the notice</Button>}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PmNotifyPromptModal;
